import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchers, ymd } from "../lib/api";
import MatchCard from "../components/MatchCard";
import LeagueTabs from "../components/LeagueTabs";
import AdSlot from "../components/AdSlot";

const buildDays = () =>
  [-3, -2, -1, 0, 1, 2, 3, 4, 5, 6].map((o) => {
    const d = new Date();
    d.setDate(d.getDate() + o);
    const label = o === 0 ? "Today" : o === -1 ? "Yesterday" : o === 1 ? "Tomorrow" : d.toLocaleDateString(undefined, { weekday: "short" });
    return { key: ymd(d), label, sub: d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) };
  });

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "live", label: "Live" },
  { id: "upcoming", label: "Upcoming" },
  { id: "ft", label: "Finished" },
];

const matchesFilter = (e, f) =>
  f === "all" ||
  (f === "live" && (e.status === "LIVE" || e.status === "HT")) ||
  (f === "upcoming" && e.status === "UPCOMING") ||
  (f === "ft" && (e.status === "FT" || e.status === "PST"));

const DateStrip = ({ days, value, onChange }) => (
  <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0" data-testid="date-strip">
    <div className="flex w-max gap-2">
      {days.map((d) => (
        <button
          key={d.key}
          data-testid={`date-${d.key}`}
          onClick={() => onChange(d.key)}
          className={`flex min-w-[84px] flex-col items-start border px-3 py-2 text-left transition-colors ${
            value === d.key ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"
          }`}
        >
          <span className="font-display text-lg leading-none">{d.label}</span>
          <span className="text-[11px] uppercase tracking-wider opacity-75">{d.sub}</span>
        </button>
      ))}
    </div>
  </div>
);

const LeagueGroup = ({ name, events }) => (
  <section data-testid={`league-group-${name}`}>
    <div className="mb-3 flex items-center justify-between border-b border-border pb-2">
      <h2 className="font-display text-2xl uppercase">{name}</h2>
      <span className="overline-eyebrow text-muted-foreground">{events.length} {events.length === 1 ? "match" : "matches"}</span>
    </div>
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {events.map((ev) => (
        <MatchCard key={ev.id} event={ev} showLeague={false} />
      ))}
    </div>
  </section>
);

export default function Matches() {
  const days = useMemo(buildDays, []);
  const [date, setDate] = useState(days[3].key);
  const [league, setLeague] = useState("all");
  const [filter, setFilter] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["matches-day", league, date],
    queryFn: () => fetchers.byDate(league, date),
    refetchInterval: 60_000,
  });

  const groups = useMemo(() => {
    const map = new Map();
    (data?.events || []).filter((e) => matchesFilter(e, filter)).forEach((e) => {
      if (!map.has(e.league)) map.set(e.league, []);
      map.get(e.league).push(e);
    });
    return [...map.entries()];
  }, [data, filter]);
  const total = groups.reduce((n, [, evs]) => n + evs.length, 0);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-6">
        <div className="overline-eyebrow text-primary">Fixtures · Live · Results</div>
        <h1 className="font-display text-5xl uppercase leading-none sm:text-6xl">The Matchday Hub</h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Every match from 20 competitions worldwide — Europe&apos;s top leagues, UEFA cups, MLS, Saudi Pro League,
          Liga 1 Indonesia and more. Scores refresh every minute.
        </p>
      </div>

      <AdSlot size="leaderboard" slotId="matches-top" className="mb-8 hidden md:flex" />
      <AdSlot size="large_mobile" slotId="matches-top-m" className="mb-8 md:hidden" />

      <div className="mb-4 space-y-3">
        <DateStrip days={days} value={date} onChange={setDate} />
        <LeagueTabs value={league} onChange={setLeague} includeAll />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2" data-testid="status-filters">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`border px-3 py-1.5 overline-eyebrow transition-colors ${
                  filter === f.id ? "border-foreground bg-foreground text-background" : "border-border bg-card hover:border-foreground"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="overline-eyebrow text-muted-foreground" data-testid="match-count">
            {isLoading ? "Loading…" : `${total} ${total === 1 ? "match" : "matches"}`}
          </span>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-4">
        <div className="space-y-10 lg:col-span-3">
          {isLoading && <div className="py-10 text-sm text-muted-foreground">Loading matches from all leagues…</div>}
          {!isLoading && total === 0 && (
            <div className="border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
              No matches for this selection. Try another day or league.
            </div>
          )}
          {groups.map(([name, evs], i) => (
            <React.Fragment key={name}>
              <LeagueGroup name={name} events={evs} />
              {i % 2 === 1 && i < groups.length - 1 && <AdSlot size="infeed" slotId={`matches-feed-${i}`} />}
            </React.Fragment>
          ))}
        </div>
        <aside className="space-y-5 lg:sticky lg:top-32 lg:self-start">
          <AdSlot size="rectangle" slotId="matches-side-1" />
          <AdSlot size="half_page" slotId="matches-side-2" className="hidden lg:flex" />
        </aside>
      </div>

      <div className="mt-10">
        <AdSlot size="billboard" slotId="matches-bottom" className="hidden lg:flex" />
        <AdSlot size="responsive" slotId="matches-bottom-m" className="lg:hidden" />
      </div>
    </div>
  );
}
